import { useContext, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import Markdown from "react-markdown";
import "../styles/prose.css";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Pagination } from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import AuthContext from "../context/AuthContext";
import useMemories from "../hooks/useMemories";
import usePageParam from "../hooks/usePageParam";
import useStoredChoice from "../hooks/useStoredChoice";
import { memoryApi } from "../services/api";
import PageHeader from "./PageHeader";
import EditMemoryModal from "./EditMemoryModal";

// Results per page: remembered in this browser (a preference, not part
// of the page's address); the page itself is in the URL (board #92).
const PAGE_SIZES = [6, 12, 24, 48];
const PAGE_SIZE_KEY = "mono.study.all.pageSize";

const MemoryList = () => {
  const [editingMemory, setEditingMemory] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deletingMemory, setDeletingMemory] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Filter and sort states. Search is submit-based (like SnippetList):
  // searchInput tracks the field, search is the submitted term.
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [filterState, setFilterState] = useState("all");
  const [sortBy, setSortBy] = useState("due");
  const [sortOrder, setSortOrder] = useState("asc");

  // The page survives a refresh, a link and Back (?page=); with the
  // remembered size, a refresh lands on the same rows.
  const [currentPage, setCurrentPage] = usePageParam();
  const [pageSize, setPageSize] = useStoredChoice(
    PAGE_SIZE_KEY,
    PAGE_SIZES,
    12,
  );

  const { isLoggedIn } = useContext(AuthContext);

  const { memories, totalCount, loading, error, errorPage, refresh } =
    useMemories({
      page: currentPage,
      pageSize,
      search,
      state: filterState,
      sortBy,
      sortOrder,
      enabled: isLoggedIn,
    });
  const totalPages = Math.ceil(totalCount / pageSize);
  // A page past the end (an old link, or rows deleted since): back to
  // page 1 — replacing the entry, so Back doesn't return to the dead page —
  // with the spinner meanwhile, never the failure. Keyed on the page the
  // ERROR came from: the current page is already 1 for one render before
  // the refetch clears that error.
  const pastTheEnd = error?.response?.status === 404 && errorPage > 1;
  useEffect(() => {
    if (pastTheEnd) setCurrentPage(1, { replace: true });
  }, [pastTheEnd, setCurrentPage]);
  const errorMessage = error
    ? error.response?.status === 401
      ? "Authentication failed. Please log in again."
      : "Failed to fetch memories. Please try again later."
    : null;

  async function handleConfirmDelete() {
    if (!deletingMemory) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await memoryApi.delete(deletingMemory.id);
      setDeletingMemory(null);
      // If that was the last item on a page past the first, step back a page —
      // otherwise currentPage now points beyond the end and the backend 404s.
      // Changing currentPage re-fetches via the params dependency, so don't
      // also refresh() (that would double-fetch).
      if (memories.length === 1 && currentPage > 1) {
        // A correction, not paging: replace the history entry.
        setCurrentPage((prev) => prev - 1, { replace: true });
      } else {
        refresh();
      }
    } catch {
      setDeleteError("Failed to delete memory. Please try again.");
    } finally {
      setDeleting(false);
    }
  }

  async function handleEdit(memoryId) {
    const memory = memories.find((m) => m.id === memoryId);
    if (memory) {
      setEditingMemory(memory);
      setShowEditModal(true);
    }
  }

  async function handleSaveEdit(memoryId, updatedData) {
    try {
      await memoryApi.update(memoryId, updatedData);
      setShowEditModal(false);
      setEditingMemory(null);
      refresh();
    } catch (err) {
      throw new Error("Failed to update memory. Please try again.", {
        cause: err,
      });
    }
  }

  // Filter handlers reset to page 1 in the same state transition as the
  // filter change — a separate "reset page" effect would fire a second
  // fetch and let the first one run with a stale page number.
  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearch(searchInput);
    setCurrentPage(1, { replace: true });
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearch("");
    setCurrentPage(1, { replace: true });
  };

  const handleCancelDelete = () => {
    if (!deleting) {
      setDeletingMemory(null);
      setDeleteError(null);
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="page-container mt-6 pt-4">
        <div className="mx-auto max-w-xl">
          <Alert>
            <AlertDescription>
              Please log in to view your memories.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  if (loading || pastTheEnd) {
    return (
      <div className="page-container mt-6 pt-4">
        <div className="py-12 text-center" role="status">
          <Loader2 className="inline size-6 animate-spin text-muted-foreground" />
          <span className="sr-only">Loading Memories...</span>
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="page-container mt-6 pt-4">
        <div className="mx-auto max-w-xl">
          <Alert variant="destructive">
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container mt-6 pt-4">
      <PageHeader
        title="Your Memories"
        subtitle="Manage your knowledge flashcards"
        badge={totalCount}
        badgeVariant="default"
      />

      {/* Filter and Sort Controls */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <form
          onSubmit={handleSearchSubmit}
          className="min-w-56 flex-1 md:max-w-xs"
        >
          <div className="flex">
            <Input
              type="search"
              placeholder="Search questions or answers..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label="Search memories"
              className="rounded-r-none"
            />
            <Button
              type="submit"
              variant="outline"
              className={cn(
                "rounded-l-none border-l-0",
                search && "rounded-r-none",
              )}
            >
              Search
            </Button>
            {search && (
              <Button
                type="button"
                variant="outline"
                className="rounded-l-none border-l-0"
                onClick={handleClearSearch}
              >
                Clear
              </Button>
            )}
          </div>
        </form>

        <NativeSelect
          aria-label="Filter by state"
          className="w-auto"
          value={filterState}
          onChange={(e) => {
            setFilterState(e.target.value);
            setCurrentPage(1, { replace: true });
          }}
        >
          <option value="all">All States</option>
          <option value="learning">Learning</option>
          <option value="review">Review</option>
          <option value="relearning">Relearning</option>
        </NativeSelect>

        <NativeSelect
          aria-label="Sort by"
          className="w-auto"
          value={sortBy}
          onChange={(e) => {
            setSortBy(e.target.value);
            setCurrentPage(1, { replace: true });
          }}
        >
          <option value="due">Sort by Due Date</option>
          <option value="difficulty">Sort by Difficulty</option>
          <option value="stability">Sort by Stability</option>
          <option value="last_review">Sort by Last Review</option>
          <option value="state">Sort by State</option>
        </NativeSelect>

        <NativeSelect
          aria-label="Sort order"
          className="w-auto"
          value={sortOrder}
          onChange={(e) => {
            setSortOrder(e.target.value);
            setCurrentPage(1, { replace: true });
          }}
        >
          <option value="asc">Ascending</option>
          <option value="desc">Descending</option>
        </NativeSelect>

        <NativeSelect
          aria-label="Results per page"
          className="w-auto"
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setCurrentPage(1, { replace: true });
          }}
        >
          {PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              {size} per page
            </option>
          ))}
        </NativeSelect>
      </div>

      {/* Results summary */}
      {(search || filterState !== "all") && totalCount > 0 && (
        <Alert className="mb-3 py-2">
          <AlertDescription>
            Found {totalCount} memories
            {search && ` matching "${search}"`}
            {filterState !== "all" && ` in ${filterState} state`}
          </AlertDescription>
        </Alert>
      )}

      {memories.length === 0 ? (
        <div className="mx-auto max-w-xl py-12 text-center">
          <h4 className="mb-3 text-lg font-semibold text-muted-foreground">
            {search || filterState !== "all"
              ? "No memories found"
              : "No memories yet"}
          </h4>
          <p className="text-muted-foreground">
            {search || filterState !== "all"
              ? "Try adjusting your filters or search term."
              : "You haven't created any memories yet. Start building your knowledge base!"}
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-3">
            {memories.map((memory) => (
              <div
                key={memory.id}
                className="flex flex-col rounded-lg border bg-card p-4 text-card-foreground shadow-xs transition-shadow hover:shadow-md"
              >
                {/* Header with metadata */}
                <div className="mb-3 flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">ID: {memory.id}</Badge>
                    {/* state is always learning/review/relearning — the
                        "new" branch was dead (see the dropped filter). */}
                    <Badge variant="secondary">{memory.state}</Badge>
                  </div>
                  <span className="hidden text-sm text-muted-foreground sm:block">
                    Knowledge: {memory.knowledge}
                  </span>
                </div>

                {/* Question Section */}
                <div className="mb-3">
                  <h6 className="mb-2 font-semibold text-primary">Question</h6>
                  <div className="border-l-2 border-primary pl-2">
                    <div className="prose-content">
                      <Markdown>{memory.question || "N/A"}</Markdown>
                    </div>
                  </div>
                </div>

                {/* Answer Section */}
                <div className="mb-3">
                  <h6 className="mb-2 font-semibold text-success">Answer</h6>
                  <div className="border-l-2 border-success pl-2">
                    <div className="prose-content">
                      <Markdown>{memory.answer || "N/A"}</Markdown>
                    </div>
                  </div>
                </div>

                {/* Responsive metadata */}
                <div className="mt-auto">
                  <div className="mb-3 grid grid-cols-3 gap-2">
                    <div>
                      <span className="block text-sm text-muted-foreground">
                        Due
                      </span>
                      <span className="text-sm font-semibold">
                        {new Date(memory.due).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="hidden sm:block">
                      <span className="block text-sm text-muted-foreground">
                        Stability
                      </span>
                      <span className="text-sm font-semibold">
                        {memory.stability?.toFixed(1) ?? "N/A"}
                      </span>
                    </div>
                    <div className="hidden md:block">
                      <span className="block text-sm text-muted-foreground">
                        Difficulty
                      </span>
                      <span className="text-sm font-semibold">
                        {memory.difficulty?.toFixed(1) ?? "N/A"}
                      </span>
                    </div>
                  </div>

                  {/* Last review - mobile friendly */}
                  <div className="mb-3 text-sm text-muted-foreground">
                    <span className="sm:hidden">
                      Last reviewed:{" "}
                      {memory.last_review
                        ? new Date(memory.last_review).toLocaleDateString()
                        : "Never"}
                    </span>
                    <span className="hidden sm:inline">
                      Last reviewed:{" "}
                      {memory.last_review
                        ? new Date(memory.last_review).toLocaleString()
                        : "Never"}
                    </span>
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-11 flex-1 md:h-8"
                      onClick={() => handleEdit(memory.id)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-11 flex-1 border-destructive/40 md:h-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => {
                        setDeleteError(null);
                        setDeletingMemory(memory);
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="mt-4 flex justify-center">
              <Pagination
                page={currentPage}
                total={totalPages}
                onChange={setCurrentPage}
                showEdges
              />
            </div>
          )}
        </>
      )}

      {/* Edit Memory Modal */}
      <EditMemoryModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setEditingMemory(null);
        }}
        memory={editingMemory}
        onSave={handleSaveEdit}
      />

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deletingMemory !== null}
        onOpenChange={(open) => !open && handleCancelDelete()}
      >
        <DialogContent
          showCloseButton={!deleting}
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => deleting && event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              Delete memory
            </DialogTitle>
            <DialogDescription>
              Delete this flashcard? Its review history goes with it. This
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteError && (
            <Alert variant="destructive">
              <AlertDescription>{deleteError}</AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={handleCancelDelete}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Loader2 className="animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MemoryList;
